import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import { finalize } from 'rxjs/operators';
import { LocationMediaService, MediaDto } from '../../../services/location-media.service';
import { ConfirmDialogV2, ConfirmDialogDataV2 } from '../../../../../shared/components/confirm-dialog.component';
import { BookingMediaUrlService } from '../../../../services/booking-media-url.service';

interface CarouselImage {
  mediaId?: string;
  preview: SafeUrl;
  url?: string;
  mediaType: string;
  displayOrder: number;
}

@Component({
    selector: 'app-location-media-section',
    templateUrl: './location-media-section.component.html',
    styleUrls: ['./location-media-section.component.css'],
    standalone: false
})
export class LocationMediaSectionComponent implements OnInit, OnDestroy {
  @Input() locationId!: string;
  @Output() mediaChanged = new EventEmitter<void>();

  logo: CarouselImage | null = null;
  banner: CarouselImage | null = null;
  carouselImages: CarouselImage[] = [];

  logoLoading = false;
  bannerLoading = false;
  carouselLoading = false;

  errorMessage: string | null = null;

  maxCarouselImages = 6;

  private previewUrls: string[] = [];

  constructor(
    private sanitizer: DomSanitizer,
    private mediaService: LocationMediaService,
    private dialog: MatDialog,
    private bookingMediaUrlService: BookingMediaUrlService
  ) { }

  ngOnInit(): void {
    if (!this.locationId) {
      console.error('locationId is required');
      return;
    }
    this.loadMedia();
  }

  ngOnDestroy(): void {
    this.previewUrls.forEach(url => {
      if (url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });
  }

  private getErrorMessage(error: any): string {
    if (!error) return '';
    
    console.log('Full error object:', error);
    
    // Si hay mensajes en el error
    if (error.messages && error.messages.length > 0) {
      return error.messages.join(', ');
    }
    
    // Si hay un mensaje directo
    if (error.message) {
      return error.message;
    }
    
    // Si hay detail (ProblemDetails de .NET)
    if (error.detail) {
      return error.detail;
    }
    
    return 'Error desconocido';
  }

  private loadMedia(): void {
    this.mediaService.getMediaByLocation(this.locationId).subscribe({
      next: (response) => {
        console.log('Get media response:', response);
        if (response.isSuccess && response.values) {
          console.log('Media values:', response.values);
          this.processMediaList(response.values);
        }
      },
      error: (err) => {
        console.error('Error loading media:', err);
        this.showError('Error al cargar las imágenes');
      }
    });
  }

  private processMediaList(mediaList: MediaDto[]): void {
    mediaList.forEach(media => {
      console.log('Processing media item:', media);
      
      // Convertir URL relativa a URL absoluta del microservicio de booking
      const absoluteUrl = this.bookingMediaUrlService.getAbsoluteMediaUrl(media.url);
      const safeUrl = this.sanitizer.bypassSecurityTrustUrl(absoluteUrl || '');
      const mediaType = media.mediaType ? media.mediaType.toUpperCase() : '';

      console.log('Media mediaId:', media.mediaId, 'type:', typeof media.mediaId, 'absoluteUrl:', absoluteUrl);

      switch (mediaType) {
        case 'LOGO':
          this.logo = {
            mediaId: media.mediaId,
            preview: safeUrl,
            url: absoluteUrl || '',
            mediaType: 'LOGO',
            displayOrder: 1
          };
          console.log('Logo set with mediaId:', this.logo.mediaId);
          break;

        case 'BANNER':
          this.banner = {
            mediaId: media.mediaId,
            preview: safeUrl,
            url: absoluteUrl || '',
            mediaType: 'BANNER',
            displayOrder: 1
          };
          console.log('Banner set with mediaId:', this.banner.mediaId);
          break;

        case 'CAROUSEL':
          if (this.carouselImages.length < this.maxCarouselImages) {
            this.carouselImages.push({
              mediaId: media.mediaId,
              preview: safeUrl,
              url: absoluteUrl || '',
              mediaType: 'CAROUSEL',
              displayOrder: media.displayOrder
            });
          }
          break;
      }
    });

    this.carouselImages.sort((a, b) => a.displayOrder - b.displayOrder);
    console.log('Processed - Logo mediaId:', this.logo ? this.logo.mediaId : 'null', 'Banner mediaId:', this.banner ? this.banner.mediaId : 'null');
  }

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      if (!this.validateImageFile(file)) {
        input.value = '';
        return;
      }

      this.uploadLogo(file);
      input.value = '';
    }
  }

  private uploadLogo(file: File): void {
    this.logoLoading = true;
    this.errorMessage = null;

    this.mediaService.uploadMedia(this.locationId, 'LOGO', file, 1)
      .pipe(finalize(() => this.logoLoading = false))
      .subscribe({
        next: (response) => {
          console.log('Upload logo response:', response);
          if (response.isSuccess && response.value) {
            const absoluteUrl = this.bookingMediaUrlService.getAbsoluteMediaUrl(response.value.url);
            const safeUrl = this.sanitizer.bypassSecurityTrustUrl(absoluteUrl || '');
            this.logo = {
              mediaId: response.value.mediaId,
              preview: safeUrl,
              url: absoluteUrl || '',
              mediaType: 'LOGO',
              displayOrder: 1
            };
            this.mediaChanged.emit();
          } else {
            const errorMsg = this.getErrorMessage(response.error);
            this.showError(errorMsg || 'Error al subir el logo');
          }
        },
        error: (err) => {
          console.error('Error uploading logo:', err);
          console.error('Error status:', err.status);
          console.error('Error statusText:', err.statusText);
          console.error('Error error:', err.error);
          
          let errorMsg = 'Error al subir el logo';
          
          // CORS error (status 0)
          if (err.status === 0) {
            errorMsg = 'Error de conexión con el servidor. Posibles causas: CORS bloqueado, servidor no disponible, o problema de autenticación.';
          }
          // Unauthorized
          else if (err.status === 401) {
            errorMsg = 'Error de autenticación. Por favor, inicie sesión nuevamente.';
          }
          // Forbidden
          else if (err.status === 403) {
            errorMsg = 'No tiene permisos para realizar esta acción.';
          }
          // Bad Request / Validation errors
          else if (err.status === 400 && err.error) {
            if (err.error.error && err.error.error.messages) {
              errorMsg = err.error.error.messages.join(', ');
            } else if (err.error.detail) {
              errorMsg = err.error.detail;
            } else if (err.error.title) {
              errorMsg = `${err.error.title}: ${err.error.detail || ''}`;
            } else if (typeof err.error === 'string') {
              errorMsg = err.error;
            }
          }
          // Server errors
          else if (err.status >= 500 && err.error) {
            if (err.error.error && err.error.error.messages) {
              errorMsg = err.error.error.messages.join(', ');
            } else if (err.error.detail) {
              errorMsg = err.error.detail;
            } else if (typeof err.error === 'string') {
              errorMsg = err.error;
            } else {
              errorMsg = `Error del servidor (${err.status})`;
            }
          }
          // Other HTTP errors
          else if (err.statusText) {
            errorMsg = `Error ${err.status}: ${err.statusText}`;
          }
          
          this.showError(errorMsg);
        }
      });
  }

  removeLogo(): void {
    console.log('removeLogo() called, logo:', this.logo);

    if (!this.logo || !this.logo.mediaId) {
      console.error('No logo or no mediaId to delete', {
        hasLogo: !!this.logo,
        mediaId: this.logo ? this.logo.mediaId : 'N/A'
      });
      return;
    }
    const dialogRef = this.dialog.open(ConfirmDialogV2, {
      width: '400px',
      maxWidth: '96vw',
      data: {
        title: 'Eliminar logo',
        message: '¿Está seguro de que desea eliminar el logo de este centro de trabajo? Esta acción no se puede deshacer.',
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
        color: 'warn',
        icon: 'delete'
      } as ConfirmDialogDataV2
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) {
        console.log('Delete logo cancelled by user');
        return;
      }

      console.log('Deleting logo with mediaId:', this.logo.mediaId);
      this.logoLoading = true;
      this.errorMessage = null;

      this.mediaService.deleteMedia(this.locationId, this.logo.mediaId)
        .pipe(finalize(() => this.logoLoading = false))
        .subscribe({
          next: (response) => {
            console.log('Delete logo response:', response);
            if (response.isSuccess) {
              this.logo = null;
              this.mediaChanged.emit();
            } else {
              const errorMsg = this.getErrorMessage(response.error);
              this.showError(errorMsg || 'Error al eliminar el logo');
            }
          },
          error: (err) => {
            console.error('Error deleting logo:', err);
            this.showError('Error al eliminar el logo');
          }
        });
    });
  }

  onBannerSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      if (!this.validateImageFile(file)) {
        input.value = '';
        return;
      }

      this.uploadBanner(file);
      input.value = '';
    }
  }

  private uploadBanner(file: File): void {
    this.bannerLoading = true;
    this.errorMessage = null;

    this.mediaService.uploadMedia(this.locationId, 'BANNER', file, 1)
      .pipe(finalize(() => this.bannerLoading = false))
      .subscribe({
        next: (response) => {
          console.log('Upload banner response:', response);
          if (response.isSuccess && response.value) {
            const absoluteUrl = this.bookingMediaUrlService.getAbsoluteMediaUrl(response.value.url);
            const safeUrl = this.sanitizer.bypassSecurityTrustUrl(absoluteUrl || '');
            this.banner = {
              mediaId: response.value.mediaId,
              preview: safeUrl,
              url: absoluteUrl || '',
              mediaType: 'BANNER',
              displayOrder: 1
            };
            this.mediaChanged.emit();
          } else {
            const errorMsg = this.getErrorMessage(response.error);
            this.showError(errorMsg || 'Error al subir el banner');
          }
        },
        error: (err) => {
          console.error('Error uploading banner:', err);
          console.error('Error status:', err.status);
          console.error('Error statusText:', err.statusText);
          console.error('Error error:', err.error);
          
          let errorMsg = 'Error al subir el banner';
          if (err.error) {
            if (err.error.error && err.error.error.messages) {
              errorMsg = err.error.error.messages.join(', ');
            } else if (err.error.detail) {
              errorMsg = err.error.detail;
            } else if (err.error.title) {
              errorMsg = `${err.error.title}: ${err.error.detail || ''}`;
            } else if (typeof err.error === 'string') {
              errorMsg = err.error;
            }
          } else if (err.statusText) {
            errorMsg = `Error ${err.status}: ${err.statusText}`;
          }
          
          this.showError(errorMsg);
        }
      });
  }

  removeBanner(): void {
    console.log('removeBanner() called, banner:', this.banner);

    if (!this.banner || !this.banner.mediaId) {
      console.error('No banner or no mediaId to delete', {
        hasBanner: !!this.banner,
        mediaId: this.banner ? this.banner.mediaId : 'N/A'
      });
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogV2, {
      width: '400px',
      maxWidth: '96vw',
      data: {
        title: 'Eliminar banner',
        message: '¿Está seguro de que desea eliminar el banner de este centro de trabajo? Esta acción no se puede deshacer.',
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
        color: 'warn',
        icon: 'delete'
      } as ConfirmDialogDataV2
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) {
        console.log('Delete banner cancelled by user');
        return;
      }

      console.log('Deleting banner with mediaId:', this.banner.mediaId);
      this.bannerLoading = true;
      this.errorMessage = null;

      this.mediaService.deleteMedia(this.locationId, this.banner.mediaId)
        .pipe(finalize(() => this.bannerLoading = false))
        .subscribe({
          next: (response) => {
            console.log('Delete banner response:', response);
            if (response.isSuccess) {
              this.banner = null;
              this.mediaChanged.emit();
            } else {
              const errorMsg = this.getErrorMessage(response.error);
              this.showError(errorMsg || 'Error al eliminar el banner');
            }
          },
          error: (err) => {
            console.error('Error deleting banner:', err);
            this.showError('Error al eliminar el banner');
          }
        });
    });
  }

  onCarouselSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const files = Array.from(input.files);

      const availableSlots = this.maxCarouselImages - this.carouselImages.length;
      const filesToAdd = files.slice(0, availableSlots);

      filesToAdd.forEach(file => {
        if (this.validateImageFile(file)) {
          this.uploadCarouselImage(file);
        }
      });

      input.value = '';

      if (files.length > filesToAdd.length) {
        this.showError(`Solo se pueden añadir ${filesToAdd.length} imágenes. Límite máximo: ${this.maxCarouselImages}`);
      }
    }
  }

  private uploadCarouselImage(file: File): void {
    this.carouselLoading = true;
    this.errorMessage = null;

    const nextDisplayOrder = this.carouselImages.length + 1;

    this.mediaService.uploadMedia(this.locationId, 'CAROUSEL', file, nextDisplayOrder)
      .pipe(finalize(() => this.carouselLoading = false))
      .subscribe({
        next: (response) => {
          console.log('Upload carousel response:', response);
          if (response.isSuccess && response.value) {
            const absoluteUrl = this.bookingMediaUrlService.getAbsoluteMediaUrl(response.value.url);
            const safeUrl = this.sanitizer.bypassSecurityTrustUrl(absoluteUrl || '');
            this.carouselImages.push({
              mediaId: response.value.mediaId,
              preview: safeUrl,
              url: absoluteUrl || '',
              mediaType: 'CAROUSEL',
              displayOrder: response.value.displayOrder
            });
            this.mediaChanged.emit();
          } else {
            const errorMsg = this.getErrorMessage(response.error);
            this.showError(errorMsg || 'Error al subir imagen del carrusel');
          }
        },
        error: (err) => {
          console.error('Error uploading carousel image:', err);
          console.error('Error status:', err.status);
          console.error('Error statusText:', err.statusText);
          console.error('Error error:', err.error);
          
          let errorMsg = 'Error al subir imagen del carrusel';
          if (err.error) {
            if (err.error.error && err.error.error.messages) {
              errorMsg = err.error.error.messages.join(', ');
            } else if (err.error.detail) {
              errorMsg = err.error.detail;
            } else if (err.error.title) {
              errorMsg = `${err.error.title}: ${err.error.detail || ''}`;
            } else if (typeof err.error === 'string') {
              errorMsg = err.error;
            }
          } else if (err.statusText) {
            errorMsg = `Error ${err.status}: ${err.statusText}`;
          }
          
          this.showError(errorMsg);
        }
      });
  }

  removeCarouselImage(index: number): void {
    console.log('removeCarouselImage() called, index:', index);
    const image = this.carouselImages[index];

    if (!image || !image.mediaId) {
      console.error('No image or no mediaId to delete', {
        hasImage: !!image,
        mediaId: image ? image.mediaId : 'N/A'
      });
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogV2, {
      width: '400px',
      maxWidth: '96vw',
      data: {
        title: 'Eliminar imagen del carrusel',
        message: '¿Está seguro de que desea eliminar esta imagen del carrusel? Esta acción no se puede deshacer.',
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
        color: 'warn',
        icon: 'delete'
      } as ConfirmDialogDataV2
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) {
        console.log('Delete carousel image cancelled by user');
        return;
      }

      console.log('Deleting carousel image with mediaId:', image.mediaId);
      this.carouselLoading = true;
      this.errorMessage = null;

      this.mediaService.deleteMedia(this.locationId, image.mediaId)
        .pipe(finalize(() => this.carouselLoading = false))
        .subscribe({
          next: (response) => {
            console.log('Delete carousel image response:', response);
            if (response.isSuccess) {
              this.carouselImages.splice(index, 1);
              this.mediaChanged.emit();
            } else {
              const errorMsg = this.getErrorMessage(response.error);
              this.showError(errorMsg || 'Error al eliminar imagen del carrusel');
            }
          },
          error: (err) => {
            console.error('Error deleting carousel image:', err);
            this.showError('Error al eliminar imagen del carrusel');
          }
        });
    });
  }

  private validateImageFile(file: File): boolean {
    if (!file.type.startsWith('image/')) {
      this.showError('El archivo seleccionado no es una imagen válida');
      return false;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      this.showError('El archivo es demasiado grande. Tamaño máximo: 5MB');
      return false;
    }

    return true;
  }

  private showError(message: string): void {
    this.errorMessage = message;
    console.error('Showing error to user:', message);

    // Auto-hide después de 15 segundos para dar tiempo a leer el error
    setTimeout(() => {
      this.errorMessage = null;
    }, 15000);
  }

  get isLoading(): boolean {
    return this.logoLoading || this.bannerLoading || this.carouselLoading;
  }

  get canAddCarouselImages(): boolean {
    return this.carouselImages.length < this.maxCarouselImages && !this.carouselLoading;
  }
  public trackByIndex(i: number) { return i; }

}
